
import { Organization } from 'common/class/data/model/organization';
import { Source } from 'common/class/data/model/source';
import { User } from 'common/class/user';
import { Serializer } from 'common/utility/serializer/serializer';
import { CognitoUser } from 'services/authentication/cognito/model/CognitoUser';
import { getCountryCode } from 'services/translation/country-code';
import { Language } from 'services/translation/language';
import { Strings } from 'src/app/common/utility/string/string';
import { StoredItems } from '../storedItems';

export enum PreferredSettings {
    MainMenuShowLabels = 1,
    WidgetMenuShowLabels
}

interface PreferredSettingsInterface {
    [PreferredSettings.MainMenuShowLabels]: boolean | undefined,
    [PreferredSettings.WidgetMenuShowLabels]: boolean | undefined
}

export class StorageBase {
    public static get SelectedLanguage(): Language | null {
        const countryCode = getCountryCode(StorageBase.getStringValue(StoredItems.Language) ?? Strings.Empty);
        return !!countryCode ? Language.get(countryCode) ?? null : null;
    }

    public static set SelectedLanguage(value: Language | null) {
        StorageBase.setValue(StoredItems.Language, value?.CountryCode.toString() ?? null);
    }

    public static get AccessToken(): string | null {
        return StorageBase.CognitoUser?.signInUserSession?.accessToken?.jwtToken ?? null
    }

    public static get RefreshToken(): string | null {
        return StorageBase.CognitoUser?.signInUserSession?.refreshToken?.token ?? null
    }

    public static get IdToken(): string | null {
        return StorageBase.CognitoUser?.signInUserSession?.idToken?.jwtToken ?? null
    }

    public static get Claims(): any {
        return StorageBase.CognitoUser?.signInUserSession?.idToken?.payload
    }

    public static get PoolId(): string | null {
        return StorageBase.CognitoUser?.pool?.userPoolId
    }

    public static get ClientId(): string | null {
        return StorageBase.CognitoUser?.pool?.clientId
    }

    public static get loggedOnUser(): User {
        let returnValue: User = StorageBase.getValue(StoredItems.LoggedOnUser, false, false) as User
        if (!returnValue || !returnValue.Id && !!StorageBase.CognitoUser?.signInUserSession?.idToken?.payload?.sub) {
            returnValue = User.fromCognitoUser(StorageBase.CognitoUser)
            StorageBase.setValue(StoredItems.LoggedOnUser, returnValue, false);
        }
        return returnValue
    }

    private static getStrippedAuthId(): string | undefined {
        if (this.loggedOnUser)
            return this.loggedOnUser.Id?.replaceAll('-', '');
        return undefined;
    }

    public static set CognitoUser(value: CognitoUser) {
        StorageBase.setValue(StoredItems.CognitoUser, value, false);
    }

    public static get CognitoUser(): CognitoUser {
        return StorageBase.getValue(StoredItems.CognitoUser, false, false) as CognitoUser
    }

    public static set OrganizationIdentifier(value: string | null) {
        StorageBase.setValue(StoredItems.OrganizationIdentifier, value, false);
    }

    public static get OrganizationIdentifier(): string | null {
        return StorageBase.getStringValue(StoredItems.OrganizationIdentifier, false)
    }

    public static set OrganizationSource(value: Array<Source> | null) {
        StorageBase.setValue(StoredItems.OrganizationSource, value, false);
    }

    public static get OrganizationSource(): Array<Source> | null {
        return StorageBase.getValue(StoredItems.OrganizationSource, false, false) as Array<Source>
    }

    public static set OrganizationInformation(value: Organization | null) {
        StorageBase.setValue(StoredItems.OrganizationInformation, value, false);
    }

    public static get OrganizationInformation(): Organization | null {
        return StorageBase.getValue(StoredItems.OrganizationInformation, false, false) as Organization
    }

    public static set Route(value: string | null) {
        StorageBase.setValue(StoredItems.Route, value, false)
    }

    public static get Route(): string | null {
        return StorageBase.getStringValue(StoredItems.Route, false)
    }

    private static initPreferredSettings(): void {
        StorageBase.preferredSettings = {
            [PreferredSettings.MainMenuShowLabels]: undefined,
            [PreferredSettings.WidgetMenuShowLabels]: undefined
        }
    }

    private static preferredSettings: PreferredSettingsInterface;
    public static setUserPreferredSettings(setting: PreferredSettings, value: any): void {
        const authId = StorageBase.getStrippedAuthId();
        if (!authId)
            return;

        if (!StorageBase.getStringValue(StoredItems.AppPreferredSettings, true, authId)) {
            StorageBase.initPreferredSettings();
        }
        StorageBase.preferredSettings[setting] = value;
        // StorageBase.setValue(StoredItems.AppPreferredSettings, JSON.stringify(StorageBase.preferredSettings), true, authId);
        StorageBase.setValue(StoredItems.AppPreferredSettings, Serializer.serialize(StorageBase.preferredSettings), true, authId);
    }

    public static getUserPreferredSettings(setting: PreferredSettings): any | undefined {
        const authId = StorageBase.getStrippedAuthId();
        if (!authId)
            return undefined;

        const preferredSettings = StorageBase.getStringValue(StoredItems.AppPreferredSettings, true, authId);
        if (!preferredSettings) {
            StorageBase.initPreferredSettings();
            StorageBase.setValue(StoredItems.AppPreferredSettings, StorageBase.preferredSettings, true, authId);
        } else {
            StorageBase.preferredSettings = Serializer.deSerialize(preferredSettings)
            // StorageBase.preferredSettings = JSON.parse(preferredSettings)
        }
        return StorageBase.preferredSettings[setting];
    }

    public static PurgeVolatile(): void {
        const keys: Array<string> = [
            StoredItems.CognitoUser,
            StoredItems.LoggedOnUser,
            StoredItems.OrganizationIdentifier
        ]

        StorageBase.Purge(keys)
    }

    public static Purge(keys: Array<string> | undefined = undefined): void {
        StorageBase.purgeStorageLocation(localStorage, keys)
        StorageBase.purgeStorageLocation(sessionStorage, keys)
    } // Purge()


    private static purgeStorageLocation(location: Storage, keys: Array<string> | undefined = undefined): void {

        keys = keys ?? Object.values(StoredItems)

        for (let i = 0; i < keys.length; i++) {
            const value: string = keys[i];
            location.removeItem(value)
        }
    }

    public static PurgeCognito(): void {
        for (let i = 0; i < localStorage.length; i++) {
            let value: string | null = localStorage.key(i);
            if (value && (value.toLowerCase().includes("cognito") || value.toLocaleLowerCase().includes("amplify"))) {
                localStorage.removeItem(value)
            }
        }
    } // PurgeCognito

    constructor() { }

    private static setValue(key: StoredItems, value: any, local: boolean = true, authId: string = ''): void {
        const destination = StorageBase.getStorageLocation(local)
        value = StorageBase.serialize(value)
        !!value ? destination.setItem(key + authId, value) : destination.removeItem(key);
    }

    private static serialize(value: any): string | null {
        return !!value
            ? Strings.isString(value) ? value : Serializer.serialize(value)
            : value
    }

    private static getStorageLocation(local: boolean): Storage {
        return local ? localStorage : sessionStorage;
    }

    private static getStringValue(key: StoredItems, local: boolean = true, authId: string = ''): string | null {
        const returnValue = StorageBase.getValue(key, local, true, authId)
        return !!returnValue ? <string>returnValue : null;
    }

    private static getValue(key: StoredItems, local: boolean = true, asString: boolean = true, authId: string = ''): string | object | null {
        let returnValue = StorageBase.getStorageLocation(local).getItem(key + authId);
        if (!asString) {
            try { returnValue = !!returnValue ? Serializer.deSerialize(returnValue) : null }
            catch { returnValue = null }
        }
        return returnValue
    }
}


